import { addRange, contains, forEach, indexOf, insert, isDefined, isEmpty, isUndefined, sign } from './../utils';
import { LayoutBase } from './LayoutBase';
import { DiagramToHyperTreeAdapter, Node } from '././../math/Graph';
import { LayoutState } from './LayoutState';
import { Dictionary, Link, Set } from './../math';
/**
 * The Sugiyama aka layered layout algorithm.
 *
 * @type {*}
 */
export class LayeredLayout extends LayoutBase {
    constructor(diagram) {
        super();
        if (isUndefined(diagram)) {
            throw new Error('Diagram is not specified.');
        }
        this.diagram = diagram;
    }
    layout(options) {
        this.transferOptions(options);
        const adapter = new DiagramToHyperTreeAdapter(this.diagram);
        const graph = adapter.convert(options);
        if (graph.isEmpty()) {
            return;
        }
        // split into connected components
        const components = graph.getConnectedComponents();
        if (isEmpty(components)) {
            return;
        }
        for (let i = 0; i < components.length; i++) {
            const component = components[i];
            this.layoutGraph(component, options);
        }
        const finalNodeSet = this.gridLayoutComponents(components);
        return new LayoutState(this.diagram, finalNodeSet);
    }
    /**
     * Initializes the runtime data properties of the layout.
     *
     * @private
     */
    _initRuntimeProperties() {
        for (let k = 0; k < this.graph.nodes.length; k++) {
            const node = this.graph.nodes[k];
            node.layer = -1;
            node.downstreamLinkCount = 0;
            node.upstreamLinkCount = 0;
            node.isVirtual = false;
            node.uBaryCenter = 0.0;
            node.dBaryCenter = 0.0;
            node.upstreamPriority = 0;
            node.downstreamPriority = 0;
            node.gridPosition = 0;
        }
    }
    _prepare(graph) {
        const current = [];
        let i, l, link;
        // defines a mapping of a node to the layer index
        const layerMap = new Dictionary();
        let layerCount = 0;
        let targetLayer, next, target;
        forEach(graph.nodes, function (node) {
            if (node.incoming.length === 0) {
                layerMap.set(node, 0);
                current.push(node);
            }
        });
        while (current.length > 0) {
            next = current.shift();
            for (i = 0; i < next.outgoing.length; i++) {
                link = next.outgoing[i];
                target = link.target;
                if (layerMap.containsKey(target)) {
                    targetLayer = Math.max(layerMap.get(next) + 1, layerMap.get(target));
                }
                else {
                    targetLayer = layerMap.get(next) + 1;
                }
                layerMap.set(target, targetLayer);
                if (targetLayer > layerCount) {
                    layerCount = targetLayer;
                }
                if (!contains(current, target)) {
                    current.push(target);
                }
            }
        }
        const sortedNodes = layerMap.keys();
        sortedNodes.sort(function (o1, o2) {
            const o1layer = layerMap.get(o1);
            const o2layer = layerMap.get(o2);
            return sign(o2layer - o1layer);
        });
        for (let n = 0; n < sortedNodes.length; ++n) {
            const node = sortedNodes[n];
            let minLayer = Number.MAX_VALUE;
            if (node.outgoing.length === 0) {
                continue;
            }
            for (l = 0; l < node.outgoing.length; ++l) {
                link = node.outgoing[l];
                minLayer = Math.min(minLayer, layerMap.get(link.target));
            }
            if (minLayer > 1) {
                layerMap.set(node, minLayer - 1);
            }
        }
        this.layers = [];
        let layer;
        for (i = 0; i < layerCount + 1; i++) {
            layer = [];
            layer.linksTo = {};
            this.layers.push(layer);
        }
        layerMap.forEach(function (node, layerVal) {
            node.layer = layerVal;
            this.layers[layerVal].push(node);
        }, this);
        // set initial grid positions
        for (l = 0; l < this.layers.length; l++) {
            layer = this.layers[l];
            for (i = 0; i < layer.length; i++) {
                layer[i].gridPosition = i;
            }
        }
    }
    /**
     * Performs the layout of a single component.
     */
    layoutGraph(graph, options) {
        if (isUndefined(graph)) {
            throw new Error('No graph given or graph analysis of the diagram failed.');
        }
        if (isDefined(options)) {
            this.transferOptions(options);
        }
        this.graph = graph;
        // sets unique indices on the nodes
        graph.setItemIndices();
        // ensures no cycles present for this layout
        const reversedEdges = graph.makeAcyclic();
        // define the runtime props being used by the layout algorithm
        this._initRuntimeProperties();
        this._prepare(graph);
        this._dummify();
        this._optimizeCrossings();
        this._swapPairs();
        this.arrangeNodes();
        this._moveThingsAround();
        this._dedummify();
        // re-reverse the links which were switched earlier
        forEach(reversedEdges, function (e) {
            if (e.points) {
                e.points.reverse();
            }
        });
    }
    setMinDist(m, _n, minDist) {
        const l = m.layer;
        const i = m.layerIndex;
        // Validate indices to prevent prototype pollution
        if (!Number.isInteger(l) || !Number.isInteger(i) || l < 0 || i < 0) {
            throw new Error('Invalid layer or index value.');
        }
        this.minDistances[l][i] = minDist;
    }
    getMinDist(m, n) {
        let dist = 0;
        const i1 = m.layerIndex, i2 = n.layerIndex, l = m.layer, min = Math.min(i1, i2), max = Math.max(i1, i2);
        // use Sum()?
        for (let k = min; k < max; ++k) {
            dist += this.minDistances[l][k];
        }
        return dist;
    }
    placeLeftToRight(leftClasses) {
        const leftPos = new Dictionary();
        let n, node;
        for (let c = 0; c < this.layers.length; ++c) {
            const classNodes = leftClasses[c];
            if (!classNodes) {
                continue;
            }
            for (n = 0; n < classNodes.length; n++) {
                node = classNodes[n];
                if (!leftPos.containsKey(node)) {
                    this.placeLeft(node, leftPos, c);
                }
            }
            // adjust class
            let d = Number.POSITIVE_INFINITY;
            for (n = 0; n < classNodes.length; n++) {
                node = classNodes[n];
                const rightSibling = this.rightSibling(node);
                if (rightSibling && this.nodeLeftClass.get(rightSibling) !== c) {
                    d = Math.min(d, leftPos.get(rightSibling) - leftPos.get(node) - this.getMinDist(node, rightSibling));
                }
            }
            if (d === Number.POSITIVE_INFINITY) {
                const D = [];
                for (n = 0; n < classNodes.length; n++) {
                    node = classNodes[n];
                    const neighbors = [];
                    addRange(neighbors, this.upNodes.get(node));
                    addRange(neighbors, this.downNodes.get(node));
                    for (let e = 0; e < neighbors.length; e++) {
                        const neighbor = neighbors[e];
                        if (this.nodeLeftClass.get(neighbor) < c) {
                            D.push(leftPos.get(neighbor) - leftPos.get(node));
                        }
                    }
                }
                D.sort();
                if (D.length === 0) {
                    d = 0;
                }
                else if (D.length % 2 === 1) {
                    d = D[this.intDiv(D.length, 2)];
                }
                else {
                    d = (D[this.intDiv(D.length, 2) - 1] + D[this.intDiv(D.length, 2)]) / 2;
                }
            }
            for (n = 0; n < classNodes.length; n++) {
                node = classNodes[n];
                leftPos.set(node, leftPos.get(node) + d);
            }
        }
        return leftPos;
    }
    placeRightToLeft(rightClasses) {
        const rightPos = new Dictionary();
        let n, node;
        for (let c = 0; c < this.layers.length; ++c) {
            const classNodes = rightClasses[c];
            if (!classNodes) {
                continue;
            }
            for (n = 0; n < classNodes.length; n++) {
                node = classNodes[n];
                if (!rightPos.containsKey(node)) {
                    this.placeRight(node, rightPos, c);
                }
            }
            // adjust class
            let d = Number.NEGATIVE_INFINITY;
            for (n = 0; n < classNodes.length; n++) {
                node = classNodes[n];
                const leftSibling = this.leftSibling(node);
                if (leftSibling && this.nodeRightClass.get(leftSibling) !== c) {
                    d = Math.max(d, rightPos.get(leftSibling) - rightPos.get(node) + this.getMinDist(leftSibling, node));
                }
            }
            if (d === Number.NEGATIVE_INFINITY) {
                const D = [];
                for (n = 0; n < classNodes.length; n++) {
                    node = classNodes[n];
                    const neighbors = [];
                    addRange(neighbors, this.upNodes.get(node));
                    addRange(neighbors, this.downNodes.get(node));
                    for (let e = 0; e < neighbors.length; e++) {
                        const neighbor = neighbors[e];
                        if (this.nodeRightClass.get(neighbor) < c) {
                            D.push(rightPos.get(node) - rightPos.get(neighbor));
                        }
                    }
                }
                D.sort();
                if (D.length === 0) {
                    d = 0;
                }
                else if (D.length % 2 === 1) {
                    d = D[this.intDiv(D.length, 2)];
                }
                else {
                    d = (D[this.intDiv(D.length, 2) - 1] + D[this.intDiv(D.length, 2)]) / 2;
                }
            }
            for (n = 0; n < classNodes.length; n++) {
                node = classNodes[n];
                rightPos.set(node, rightPos.get(node) + d);
            }
        }
        return rightPos;
    }
    _getLeftWing() {
        const leftWing = { value: null };
        const result = this.computeClasses(leftWing, 1);
        this.nodeLeftClass = leftWing.value;
        return result;
    }
    _getRightWing() {
        const rightWing = { value: null };
        const result = this.computeClasses(rightWing, -1);
        this.nodeRightClass = rightWing.value;
        return result;
    }
    computeClasses(wingPair, d) {
        let currentWing = 0;
        const wing = wingPair.value = new Dictionary();
        for (let l = 0; l < this.layers.length; ++l) {
            currentWing = l;
            const layer = this.layers[l];
            for (let n = d === 1 ? 0 : layer.length - 1; n >= 0 && n < layer.length; n += d) {
                const node = layer[n];
                if (!wing.containsKey(node)) {
                    wing.set(node, currentWing);
                    if (node.isVirtual) {
                        const ndsinl = this._nodesInLink(node);
                        for (let kk = 0; kk < ndsinl.length; kk++) {
                            const vnode = ndsinl[kk];
                            wing.set(vnode, currentWing);
                        }
                    }
                }
                else {
                    currentWing = wing.get(node);
                }
            }
        }
        const wings = [];
        for (let i = 0; i < this.layers.length; i++) {
            wings.push(null);
        }
        wing.forEach(function (node, classIndex) {
            if (wings[classIndex] === null) {
                wings[classIndex] = [];
            }
            wings[classIndex].push(node);
        });
        return wings;
    }
    _isVerticalLayout() {
        return this.options.subtype.toLowerCase() === 'up' || this.options.subtype.toLowerCase() === 'down' || this.options.subtype.toLowerCase() === 'vertical';
    }
    _isHorizontalLayout() {
        return this.options.subtype.toLowerCase() === 'right' || this.options.subtype.toLowerCase() === 'left' || this.options.subtype.toLowerCase() === 'horizontal';
    }
    _isIncreasingLayout() {
        // meaning that the visiting of the layers goes in the natural order of increasing layer index
        return this.options.subtype.toLowerCase() === 'right' || this.options.subtype.toLowerCase() === 'down';
    }
    _moveThingsAround() {
        let i, l, node, layer, n, w;
        // sort the layers by their grid position
        for (l = 0; l < this.layers.length; ++l) {
            layer = this.layers[l];
            layer.sort(this._gridPositionComparer.bind(this));
        }
        this.minDistances = [];
        for (l = 0; l < this.layers.length; ++l) {
            layer = this.layers[l];
            this.minDistances[l] = [];
            for (n = 0; n < layer.length; ++n) {
                node = layer[n];
                node.layerIndex = n;
                this.minDistances[l][n] = this.options.nodeDistance;
                if (n < layer.length - 1) {
                    if (this._isVerticalLayout()) {
                        this.minDistances[l][n] += (node.width + layer[n + 1].width) / 2;
                    }
                    else {
                        this.minDistances[l][n] += (node.height + layer[n + 1].height) / 2;
                    }
                }
            }
        }
        this.downNodes = new Dictionary();
        this.upNodes = new Dictionary();
        forEach(this.graph.nodes, function (gNode) {
            this.downNodes.set(gNode, []);
            this.upNodes.set(gNode, []);
        }, this);
        forEach(this.graph.links, function (link) {
            const origin = link.source;
            const dest = link.target;
            let down = null, up = null;
            if (origin.layer > dest.layer) {
                down = link.source;
                up = link.target;
            }
            else {
                up = link.source;
                down = link.target;
            }
            this.downNodes.get(up).push(down);
            this.upNodes.get(down).push(up);
        }, this);
        this.downNodes.forEachValue(function (list) {
            list.sort(this._gridPositionComparer);
        }, this);
        this.upNodes.forEachValue(function (list) {
            list.sort(this._gridPositionComparer);
        }, this);
        for (l = 0; l < this.layers.length - 1; ++l) {
            layer = this.layers[l];
            for (w = 0; w < layer.length - 1; w++) {
                const currentNode = layer[w];
                if (!currentNode.isVirtual) {
                    continue;
                }
                const currDown = this.downNodes.get(currentNode)[0];
                if (!currDown.isVirtual) {
                    continue;
                }
                for (n = w + 1; n < layer.length; ++n) {
                    node = layer[n];
                    if (!node.isVirtual) {
                        continue;
                    }
                    const downNode = this.downNodes.get(node)[0];
                    if (!downNode.isVirtual) {
                        continue;
                    }
                    if (currDown.gridPosition > downNode.gridPosition) {
                        const pos = currDown.gridPosition;
                        currDown.gridPosition = downNode.gridPosition;
                        downNode.gridPosition = pos;
                        const i1 = currDown.layerIndex;
                        const i2 = downNode.layerIndex;
                        this.layers[l + 1][i1] = downNode;
                        this.layers[l + 1][i2] = currDown;
                        currDown.layerIndex = i2;
                        downNode.layerIndex = i1;
                    }
                }
            }
        }
        const leftClasses = this._getLeftWing();
        const rightClasses = this._getRightWing();
        const leftPos = this.placeLeftToRight(leftClasses);
        const rightPos = this.placeRightToLeft(rightClasses);
        const x = new Dictionary();
        forEach(this.graph.nodes, function (gNode) {
            x.set(gNode, (leftPos.get(gNode) + rightPos.get(gNode)) / 2);
        });
        const order = new Dictionary();
        const placed = new Dictionary();
        for (l = 0; l < this.layers.length; ++l) {
            layer = this.layers[l];
            let sequenceStart = -1; // , sequenceEnd = -1;
            for (n = 0; n < layer.length; ++n) {
                node = layer[n];
                order.set(node, 0);
                placed.set(node, false);
                if (node.isVirtual) {
                    if (sequenceStart === -1) {
                        sequenceStart = n;
                    }
                    else if (sequenceStart === n - 1) {
                        sequenceStart = n;
                    }
                    else {
                        // sequenceEnd = n;
                        order.set(layer[sequenceStart], 0);
                        if (x.get(node) - x.get(layer[sequenceStart]) === this.getMinDist(layer[sequenceStart], node)) {
                            placed.set(layer[sequenceStart], true);
                        }
                        else {
                            placed.set(layer[sequenceStart], false);
                        }
                        sequenceStart = n;
                    }
                }
            }
        }
        const directions = [1, -1];
        forEach(directions, function (d) {
            const start = d === 1 ? 0 : this.layers.length - 1;
            for (let ll = start; ll >= 0 && ll < this.layers.length; ll += d) {
                const layer2 = this.layers[ll];
                let virtualStartIndex = this._firstVirtualNode(layer2);
                let virtualStart = null;
                let sequence = null;
                if (virtualStartIndex !== -1) {
                    virtualStart = layer2[virtualStartIndex];
                    sequence = [];
                    for (i = 0; i < virtualStartIndex; i++) {
                        sequence.push(layer2[i]);
                    }
                }
                else {
                    virtualStart = null;
                    sequence = layer2;
                }
                if (sequence.length > 0) {
                    this._sequencer(x, null, virtualStart, d, sequence);
                    for (i = 0; i < sequence.length - 1; ++i) {
                        this.setMinDist(sequence[i], sequence[i + 1], x.get(sequence[i + 1]) - x.get(sequence[i]));
                    }
                    if (virtualStart) {
                        this.setMinDist(sequence[sequence.length - 1], virtualStart, x.get(virtualStart) - x.get(sequence[sequence.length - 1]));
                    }
                }
                while (virtualStart) {
                    const virtualEnd = this.nextVirtualNode(layer2, virtualStart);
                    if (!virtualEnd) {
                        virtualStartIndex = virtualStart.layerIndex;
                        sequence = [];
                        for (i = virtualStartIndex + 1; i < layer2.length; i++) {
                            sequence.push(layer2[i]);
                        }
                        if (sequence.length > 0) {
                            this._sequencer(x, virtualStart, null, d, sequence);
                            for (i = 0; i < sequence.length - 1; ++i) {
                                this.setMinDist(sequence[i], sequence[i + 1], x.get(sequence[i + 1]) - x.get(sequence[i]));
                            }
                            this.setMinDist(virtualStart, sequence[0], x.get(sequence[0]) - x.get(virtualStart));
                        }
                    }
                    else if (order.get(virtualStart) === d) {
                        virtualStartIndex = virtualStart.layerIndex;
                        const virtualEndIndex = virtualEnd.layerIndex;
                        sequence = [];
                        for (i = virtualStartIndex + 1; i < virtualEndIndex; i++) {
                            sequence.push(layer2[i]);
                        }
                        if (sequence.length > 0) {
                            this._sequencer(x, virtualStart, virtualEnd, d, sequence);
                        }
                        placed.set(virtualStart, true);
                    }
                    virtualStart = virtualEnd;
                }
                this.adjustDirections(ll, d, order, placed);
            }
        }, this);
        const fromLayerIndex = this._isIncreasingLayout() ? 0 : this.layers.length - 1;
        const reachedFinalLayerIndex = function (k, ctx) {
            if (ctx._isIncreasingLayout()) {
                return k < ctx.layers.length;
            }
            else {
                return k >= 0;
            }
        };
        const layerIncrement = this._isIncreasingLayout() ? +1 : -1;
        let offset = 0;
        /**
         * Calcs the max height of the given layer.
         */
        function maximumHeight(layer2, ctx) {
            let height = Number.MIN_VALUE;
            for (let nn = 0; nn < layer2.length; ++nn) {
                const node2 = layer2[nn];
                if (ctx._isVerticalLayout()) {
                    height = Math.max(height, node2.height);
                }
                else {
                    height = Math.max(height, node2.width);
                }
            }
            return height;
        }
        for (i = fromLayerIndex; reachedFinalLayerIndex(i, this); i += layerIncrement) {
            layer = this.layers[i];
            const height = maximumHeight(layer, this);
            for (n = 0; n < layer.length; ++n) {
                node = layer[n];
                if (this._isVerticalLayout()) {
                    node.x = x.get(node);
                    node.y = offset + height / 2;
                }
                else {
                    node.x = offset + height / 2;
                    node.y = x.get(node);
                }
            }
            offset += this.options.layerSeparation + height;
        }
    }
    adjustDirections(l, d, order, placed) {
        if (l + d < 0 || l + d >= this.layers.length) {
            return;
        }
        let prevBridge = null, prevBridgeTarget = null;
        const layer = this.layers[l + d];
        for (let n = 0; n < layer.length; ++n) {
            const nextBridge = layer[n];
            if (nextBridge.isVirtual) {
                const nextBridgeTarget = this.getNeighborOnLayer(nextBridge, l);
                if (nextBridgeTarget.isVirtual) {
                    if (prevBridge) {
                        let p = placed.get(prevBridgeTarget);
                        const clayer = this.layers[l];
                        const i1 = prevBridgeTarget.layerIndex;
                        const i2 = nextBridgeTarget.layerIndex;
                        for (let i = i1 + 1; i < i2; ++i) {
                            if (clayer[i].isVirtual) {
                                p = p && placed.get(clayer[i]);
                            }
                        }
                        if (p) {
                            order.set(prevBridge, d);
                            const j1 = prevBridge.layerIndex;
                            const j2 = nextBridge.layerIndex;
                            for (let j = j1 + 1; j < j2; ++j) {
                                if (layer[j].isVirtual) {
                                    order.set(layer[j], d);
                                }
                            }
                        }
                    }
                    prevBridge = nextBridge;
                    prevBridgeTarget = nextBridgeTarget;
                }
            }
        }
    }
    getNeighborOnLayer(node, l) {
        let neighbor = this.upNodes.get(node)[0];
        if (neighbor.layer === l) {
            return neighbor;
        }
        neighbor = this.downNodes.get(node)[0];
        if (neighbor.layer === l) {
            return neighbor;
        }
        return null;
    }
    _sequencer(x, virtualStart, virtualEnd, dir, sequence) {
        if (sequence.length === 1) {
            this._sequenceSingle(x, virtualStart, virtualEnd, dir, sequence[0]);
        }
        if (sequence.length > 1) {
            const r = sequence.length, t = this.intDiv(r, 2);
            this._sequencer(x, virtualStart, virtualEnd, dir, sequence.slice(0, t));
            this._sequencer(x, virtualStart, virtualEnd, dir, sequence.slice(t));
            this.combineSequences(x, virtualStart, virtualEnd, dir, sequence);
        }
    }
    _sequenceSingle(x, virtualStart, virtualEnd, dir, node) {
        const neighbors = dir === -1 ? this.downNodes.get(node) : this.upNodes.get(node);
        const n = neighbors.length;
        if (n !== 0) {
            if (n % 2 === 1) {
                x.set(node, x.get(neighbors[this.intDiv(n, 2)]));
            }
            else {
                x.set(node, (x.get(neighbors[this.intDiv(n, 2) - 1]) + x.get(neighbors[this.intDiv(n, 2)])) / 2);
            }
            if (virtualStart) {
                x.set(node, Math.max(x.get(node), x.get(virtualStart) + this.getMinDist(virtualStart, node)));
            }
            if (virtualEnd) {
                x.set(node, Math.min(x.get(node), x.get(virtualEnd) - this.getMinDist(node, virtualEnd)));
            }
        }
    }
    combineSequences(x, virtualStart, virtualEnd, dir, sequence) {
        const r = sequence.length, t = this.intDiv(r, 2);
        // collect left changes
        const leftHeap = [];
        let i, c, n, neighbors, neighbor, pair;
        for (i = 0; i < t; ++i) {
            c = 0;
            neighbors = dir === -1 ? this.downNodes.get(sequence[i]) : this.upNodes.get(sequence[i]);
            for (n = 0; n < neighbors.length; ++n) {
                neighbor = neighbors[n];
                if (x.get(neighbor) >= x.get(sequence[i])) {
                    c++;
                }
                else {
                    c--;
                    leftHeap.push({ k: x.get(neighbor) + this.getMinDist(sequence[i], sequence[t - 1]), v: 2 });
                }
            }
            leftHeap.push({ k: x.get(sequence[i]) + this.getMinDist(sequence[i], sequence[t - 1]), v: c });
        }
        if (virtualStart) {
            leftHeap.push({ k: x.get(virtualStart) + this.getMinDist(virtualStart, sequence[t - 1]), v: Number.MAX_VALUE });
        }
        leftHeap.sort(this._positionDescendingComparer.bind(this));
        // collect right changes
        const rightHeap = [];
        for (i = t; i < r; ++i) {
            c = 0;
            neighbors = dir === -1 ? this.downNodes.get(sequence[i]) : this.upNodes.get(sequence[i]);
            for (n = 0; n < neighbors.length; ++n) {
                neighbor = neighbors[n];
                if (x.get(neighbor) <= x.get(sequence[i])) {
                    c++;
                }
                else {
                    c--;
                    rightHeap.push({ k: x.get(neighbor) - this.getMinDist(sequence[i], sequence[t]), v: 2 });
                }
            }
            rightHeap.push({ k: x.get(sequence[i]) - this.getMinDist(sequence[i], sequence[t]), v: c });
        }
        if (virtualEnd) {
            rightHeap.push({ k: x.get(virtualEnd) - this.getMinDist(virtualEnd, sequence[t]), v: Number.MAX_VALUE });
        }
        rightHeap.sort(this._positionAscendingComparer.bind(this));
        let leftRes = 0, rightRes = 0;
        const m = this.getMinDist(sequence[t - 1], sequence[t]);
        while (x.get(sequence[t]) - x.get(sequence[t - 1]) < m) {
            if (leftRes < rightRes) {
                if (leftHeap.length === 0) {
                    x.set(sequence[t - 1], x.get(sequence[t]) - m);
                    break;
                }
                else {
                    pair = leftHeap.shift();
                    leftRes = leftRes + pair.v;
                    x.set(sequence[t - 1], pair.k);
                    x.set(sequence[t - 1], Math.max(x.get(sequence[t - 1]), x.get(sequence[t]) - m));
                }
            }
            else {
                if (rightHeap.length === 0) {
                    x.set(sequence[t], x.get(sequence[t - 1]) + m);
                    break;
                }
                else {
                    pair = rightHeap.shift();
                    rightRes = rightRes + pair.v;
                    x.set(sequence[t], pair.k);
                    x.set(sequence[t], Math.min(x.get(sequence[t]), x.get(sequence[t - 1]) + m));
                }
            }
        }
        for (i = t - 2; i >= 0; i--) {
            x.set(sequence[i], Math.min(x.get(sequence[i]), x.get(sequence[t - 1]) - this.getMinDist(sequence[i], sequence[t - 1])));
        }
        for (i = t + 1; i < r; i++) {
            x.set(sequence[i], Math.max(x.get(sequence[i]), x.get(sequence[t]) + this.getMinDist(sequence[i], sequence[t])));
        }
    }
    placeLeft(node, leftPos, leftClass) {
        let pos = Number.NEGATIVE_INFINITY;
        forEach(this._getComposite(node), function (v) {
            const leftSibling = this.leftSibling(v);
            if (leftSibling && this.nodeLeftClass.get(leftSibling) === this.nodeLeftClass.get(v)) {
                if (!leftPos.containsKey(leftSibling)) {
                    this.placeLeft(leftSibling, leftPos, leftClass);
                }
                pos = Math.max(pos, leftPos.get(leftSibling) + this.getMinDist(leftSibling, v));
            }
        }, this);
        if (pos === Number.NEGATIVE_INFINITY) {
            pos = 0;
        }
        forEach(this._getComposite(node), function (v) {
            leftPos.set(v, pos);
        });
    }
    placeRight(node, rightPos, rightClass) {
        let pos = Number.POSITIVE_INFINITY;
        forEach(this._getComposite(node), function (v) {
            const rightSibling = this.rightSibling(v);
            if (rightSibling && this.nodeRightClass.get(rightSibling) === this.nodeRightClass.get(v)) {
                if (!rightPos.containsKey(rightSibling)) {
                    this.placeRight(rightSibling, rightPos, rightClass);
                }
                pos = Math.min(pos, rightPos.get(rightSibling) - this.getMinDist(v, rightSibling));
            }
        }, this);
        if (pos === Number.POSITIVE_INFINITY) {
            pos = 0;
        }
        forEach(this._getComposite(node), function (v) {
            rightPos.set(v, pos);
        });
    }
    leftSibling(node) {
        const layer = this.layers[node.layer], layerIndex = node.layerIndex;
        return layerIndex === 0 ? null : layer[layerIndex - 1];
    }
    rightSibling(node) {
        const layer = this.layers[node.layer];
        const layerIndex = node.layerIndex;
        return layerIndex === layer.length - 1 ? null : layer[layerIndex + 1];
    }
    _getComposite(node) {
        return node.isVirtual ? this._nodesInLink(node) : [node];
    }
    arrangeNodes() {
        let i, l, ni, layer, node;
        // Initialize node's base priority
        for (l = 0; l < this.layers.length; l++) {
            layer = this.layers[l];
            for (ni = 0; ni < layer.length; ni++) {
                node = layer[ni];
                node.upstreamPriority = node.upstreamLinkCount;
                node.downstreamPriority = node.downstreamLinkCount;
            }
        }
        // Layout is invoked after MinimizeCrossings
        // so we may assume node's barycenters are initially correct
        const maxLayoutIterations = 2;
        for (let it = 0; it < maxLayoutIterations; it++) {
            for (i = this.layers.length - 1; i >= 1; i--) {
                this.layoutLayer(false, i);
            }
            for (i = 0; i < this.layers.length - 1; i++) {
                this.layoutLayer(true, i);
            }
        }
        // Offset the whole structure so that there are no gridPositions < 0
        let gridPos = Number.MAX_VALUE;
        for (l = 0; l < this.layers.length; l++) {
            layer = this.layers[l];
            for (ni = 0; ni < layer.length; ni++) {
                node = layer[ni];
                gridPos = Math.min(gridPos, node.gridPosition);
            }
        }
        if (gridPos < 0) {
            for (l = 0; l < this.layers.length; l++) {
                layer = this.layers[l];
                for (ni = 0; ni < layer.length; ni++) {
                    node = layer[ni];
                    node.gridPosition = node.gridPosition - gridPos;
                }
            }
        }
    }
    /// <summary>
    /// Layout of a single layer.
    /// </summary>
    /// <param name="layerIndex">The layer to organize.</param>
    /// <param name="movingDownwards">If set to <c>true</c> we move down in the layer stack.</param>
    /// <seealso cref="OptimizeCrossings()"/>
    layoutLayer(down, layer) {
        let iconsidered;
        let considered;
        if (down) {
            considered = this.layers[iconsidered = layer + 1];
        }
        else {
            considered = this.layers[iconsidered = layer - 1];
        }
        // list containing the nodes in the considered layer sorted by priority
        const sorted = [];
        for (let n = 0; n < considered.length; n++) {
            sorted.push(considered[n]);
        }
        sorted.sort(function (n1, n2) {
            const n1Priority = (n1.upstreamPriority + n1.downstreamPriority) / 2;
            const n2Priority = (n2.upstreamPriority + n2.downstreamPriority) / 2;
            if (Math.abs(n1Priority - n2Priority) < 0.0001) {
                return 0;
            }
            if (n1Priority < n2Priority) {
                return 1;
            }
            return -1;
        });
        // each node strives for its barycenter; high priority nodes start first
        forEach(sorted, function (node) {
            let nodeGridPos = node.gridPosition;
            const nodeBaryCenter = this.calcBaryCenter(node);
            const nodePriority = (node.upstreamPriority + node.downstreamPriority) / 2;
            if (Math.abs(nodeGridPos - nodeBaryCenter) < 0.0001) {
                // This node is exactly at its barycenter -> perfect
                return;
            }
            if (Math.abs(nodeGridPos - nodeBaryCenter) < 0.25 + 0.0001) {
                // This node is close enough to the barycenter -> should work
                return;
            }
            if (nodeGridPos < nodeBaryCenter) {
                // Try to move the node to the right in an
                // attempt to reach its barycenter
                while (nodeGridPos < nodeBaryCenter) {
                    if (!this.moveRight(node, considered, nodePriority)) {
                        break;
                    }
                    nodeGridPos = node.gridPosition;
                }
            }
            else {
                // Try to move the node to the left in an
                // attempt to reach its barycenter
                while (nodeGridPos > nodeBaryCenter) {
                    if (!this.moveLeft(node, considered, nodePriority)) {
                        break;
                    }
                    nodeGridPos = node.gridPosition;
                }
            }
        }, this);
        // after the layer has been rearranged we need to recalculate the barycenters
        // of the nodes in the surrounding layers
        if (iconsidered > 0) {
            this.calcDownData(iconsidered - 1);
        }
        if (iconsidered < this.layers.length - 1) {
            this.calcUpData(iconsidered + 1);
        }
    }
    /// <summary>
    /// Moves the node to the right and returns <c>true</c> if this was possible.
    /// </summary>
    /// <param name="node">The node.</param>
    /// <param name="layer">The layer.</param>
    /// <returns>Returns <c>true</c> if the shift was possible, otherwise <c>false</c>.</returns>
    moveRight(node, layer, priority) {
        const index = indexOf(layer, node);
        if (index === layer.length - 1) {
            // this is the last node in the layer, so we can move to the right without troubles
            node.gridPosition = node.gridPosition + 0.5;
            return true;
        }
        const rightNode = layer[index + 1];
        const rightNodePriority = (rightNode.upstreamPriority + rightNode.downstreamPriority) / 2;
        // check if there is space between the right and the current node
        if (rightNode.gridPosition > node.gridPosition + 1) {
            node.gridPosition = node.gridPosition + 0.5;
            return true;
        }
        // we have reached a node with higher priority; no movement is allowed
        if (rightNodePriority > priority ||
            Math.abs(rightNodePriority - priority) < 0.0001) {
            return false;
        }
        // the right node has lower priority - try to move it
        if (this.moveRight(rightNode, layer, priority)) {
            node.gridPosition = node.gridPosition + 0.5;
            return true;
        }
        return false;
    }
    /// <summary>
    /// Moves the node to the left and returns <c>true</c> if this was possible.
    /// </summary>
    /// <param name="node">The node.</param>
    /// <param name="layer">The layer.</param>
    /// <returns>Returns <c>true</c> if the shift was possible, otherwise <c>false</c>.</returns>
    moveLeft(node, layer, priority) {
        const index = indexOf(layer, node);
        if (index === 0) {
            // this is the last node in the layer, so we can move to the left without troubles
            node.gridPosition = node.gridPosition - 0.5;
            return true;
        }
        const leftNode = layer[index - 1];
        const leftNodePriority = (leftNode.upstreamPriority + leftNode.downstreamPriority) / 2;
        // check if there is space between the left and the current node
        if (leftNode.gridPosition < node.gridPosition - 1) {
            node.gridPosition = node.gridPosition - 0.5;
            return true;
        }
        // we have reached a node with higher priority; no movement is allowed
        if (leftNodePriority > priority ||
            Math.abs(leftNodePriority - priority) < 0.0001) {
            return false;
        }
        // The left node has lower priority - try to move it
        if (this.moveLeft(leftNode, layer, priority)) {
            node.gridPosition = node.gridPosition - 0.5;
            return true;
        }
        return false;
    }
    mapVirtualNode(node, link) {
        this.nodeToLinkMap.set(node, link);
        if (!this.linkToNodeMap.containsKey(link)) {
            this.linkToNodeMap.set(link, []);
        }
        this.linkToNodeMap.get(link).push(node);
    }
    _nodesInLink(node) {
        return this.linkToNodeMap.get(this.nodeToLinkMap.get(node));
    }
    /// <summary>
    /// Inserts dummy nodes to break long links.
    /// </summary>
    _dummify() {
        this.linkToNodeMap = new Dictionary();
        this.nodeToLinkMap = new Dictionary();
        const links = this.graph.links.slice(0);
        const layers = this.layers;
        let layer, pos, newNode, node, r, newLink, i, l;
        const addLinkBetweenLayers = function (upLayer, downLayer, link) {
            layers[upLayer].linksTo[downLayer] = layers[upLayer].linksTo[downLayer] || [];
            layers[upLayer].linksTo[downLayer].push(link);
        };
        for (l = 0; l < links.length; l++) {
            const link = links[l];
            const o = link.source;
            const d = link.target;
            const oLayer = o.layer;
            const dLayer = d.layer;
            const oPos = o.gridPosition;
            const dPos = d.gridPosition;
            const step = (dPos - oPos) / Math.abs(dLayer - oLayer);
            let p = o;
            if (oLayer - dLayer > 1) {
                for (i = oLayer - 1; i > dLayer; i--) {
                    newNode = new Node();
                    newNode.x = o.x;
                    newNode.y = o.y;
                    newNode.width = o.width / 100;
                    newNode.height = o.height / 100;
                    layer = layers[i];
                    pos = (i - dLayer) * step + oPos;
                    if (pos > layer.length) {
                        pos = layer.length;
                    }
                    // check if origin and dest are both last
                    if (oPos >= layers[oLayer].length - 1 &&
                        dPos >= layers[dLayer].length - 1) {
                        pos = layer.length;
                    }
                    else if (oPos === 0 && dPos === 0) { // check if origin and destination are both first
                        pos = 0;
                    }
                    newNode.layer = i;
                    newNode.uBaryCenter = 0.0;
                    newNode.dBaryCenter = 0.0;
                    newNode.upstreamLinkCount = 0;
                    newNode.downstreamLinkCount = 0;
                    newNode.gridPosition = pos;
                    newNode.isVirtual = true;
                    insert(layer, newNode, pos);
                    // translate rightwards nodes' positions
                    for (r = pos + 1; r < layer.length; r++) {
                        node = layer[r];
                        node.gridPosition = node.gridPosition + 1;
                    }
                    newLink = new Link(p, newNode);
                    newLink.depthOfDumminess = 0;
                    addLinkBetweenLayers(i - 1, i, newLink);
                    p = newNode;
                    // add the new node and the new link to the graph
                    this.graph._addNode(newNode);
                    this.graph.addLink(newLink);
                    newNode.index = this.graph.nodes.length - 1;
                    this.mapVirtualNode(newNode, link);
                }
                // set the origin of the real arrow to the last dummy
                addLinkBetweenLayers(dLayer - 1, dLayer, newLink);
                link.changeSource(p);
                link.depthOfDumminess = oLayer - dLayer - 1;
            }
            else if (oLayer - dLayer < -1) {
                for (i = oLayer + 1; i < dLayer; i++) {
                    newNode = new Node();
                    newNode.x = o.x;
                    newNode.y = o.y;
                    newNode.width = o.width / 100;
                    newNode.height = o.height / 100;
                    layer = layers[i];
                    pos = (i - oLayer) * step + oPos;
                    if (pos > layer.length) {
                        pos = layer.length;
                    }
                    // check if origin and dest are both last
                    if (oPos >= layers[oLayer].length - 1 &&
                        dPos >= layers[dLayer].length - 1) {
                        pos = layer.length;
                    }
                    else if (oPos === 0 && dPos === 0) { // check if origin and destination are both first
                        pos = 0;
                    }
                    newNode.layer = i;
                    newNode.uBaryCenter = 0.0;
                    newNode.dBaryCenter = 0.0;
                    newNode.upstreamLinkCount = 0;
                    newNode.downstreamLinkCount = 0;
                    newNode.gridPosition = pos;
                    newNode.isVirtual = true;
                    pos = Math.floor(pos); // truncates to int
                    insert(layer, newNode, pos);
                    // translate rightwards nodes' positions
                    for (r = pos + 1; r < layer.length; r++) {
                        node = layer[r];
                        node.gridPosition = node.gridPosition + 1;
                    }
                    newLink = new Link(p, newNode);
                    newLink.depthOfDumminess = 0;
                    addLinkBetweenLayers(i - 1, i, newLink);
                    p = newNode;
                    // add the new node and the new link to the graph
                    this.graph._addNode(newNode);
                    this.graph.addLink(newLink);
                    newNode.index = this.graph.nodes.length - 1;
                    this.mapVirtualNode(newNode, link);
                }
                addLinkBetweenLayers(dLayer - 1, dLayer, link);
                // Set the origin of the real arrow to the last dummy
                link.changeSource(p);
                link.depthOfDumminess = dLayer - oLayer - 1;
            }
            else {
                addLinkBetweenLayers(oLayer, dLayer, link);
            }
        }
    }
    /// <summary>
    /// Removes the dummy nodes inserted earlier to break long links.
    /// </summary>
    /// <remarks>The virtual nodes are effectively turned into intermediate connection points.</remarks>
    _dedummify() {
        let dedum = true;
        while (dedum) {
            dedum = false;
            for (let l = 0; l < this.graph.links.length; l++) {
                const link = this.graph.links[l];
                if (!link.depthOfDumminess) {
                    continue;
                }
                const points = [];
                // add points in reverse order
                points.unshift({ x: link.target.x, y: link.target.y });
                points.unshift({ x: link.source.x, y: link.source.y });
                // _dedummify the link
                let temp = link;
                const depthOfDumminess = link.depthOfDumminess;
                for (let d = 0; d < depthOfDumminess; d++) {
                    const node = temp.source;
                    const prevLink = node.incoming[0];
                    points.unshift({ x: prevLink.source.x, y: prevLink.source.y });
                    temp = prevLink;
                }
                // restore the original link origin
                link.changeSource(temp.source);
                // reset dummification flag
                link.depthOfDumminess = 0;
                // note that we only need the intermediate points, floating links have been dropped in the analysis
                if (points.length > 2) {
                    // first and last are the endpoints
                    points.splice(0, 1);
                    points.splice(points.length - 1);
                    link.points = points;
                }
                else {
                    link.points = [];
                }
                // we are not going to delete the dummy elements;
                // they won't be needed anymore anyway.
                dedum = true;
                break;
            }
        }
    }
    /// <summary>
    /// Optimizes/reduces the crossings between the layers by turning the crossing problem into a (combinatorial) number ordering problem.
    /// </summary>
    _optimizeCrossings() {
        let moves = -1, i;
        const maxIterations = 3;
        let iter = 0;
        while (moves !== 0) {
            if (iter++ > maxIterations) {
                break;
            }
            moves = 0;
            for (i = this.layers.length - 1; i >= 1; i--) {
                moves += this.optimizeLayerCrossings(false, i);
            }
            for (i = 0; i < this.layers.length - 1; i++) {
                moves += this.optimizeLayerCrossings(true, i);
            }
        }
    }
    calcUpData(layer) {
        if (layer === 0) {
            return;
        }
        const considered = this.layers[layer];
        let i, l, link;
        const upLayer = new Set();
        const temp = this.layers[layer - 1];
        for (i = 0; i < temp.length; i++) {
            upLayer.add(temp[i]);
        }
        for (i = 0; i < considered.length; i++) {
            const node = considered[i];
            // calculate barycenter
            let sum = 0;
            let total = 0;
            for (l = 0; l < node.incoming.length; l++) {
                link = node.incoming[l];
                if (upLayer.contains(link.source)) {
                    total++;
                    sum += link.source.gridPosition;
                }
            }
            for (l = 0; l < node.outgoing.length; l++) {
                link = node.outgoing[l];
                if (upLayer.contains(link.target)) {
                    total++;
                    sum += link.target.gridPosition;
                }
            }
            if (total > 0) {
                node.uBaryCenter = sum / total;
                node.upstreamLinkCount = total;
            }
            else {
                node.uBaryCenter = i;
                node.upstreamLinkCount = 0;
            }
        }
    }
    calcDownData(layer) {
        if (layer === this.layers.length - 1) {
            return;
        }
        const considered = this.layers[layer];
        let i, l, link;
        const downLayer = new Set();
        const temp = this.layers[layer + 1];
        for (i = 0; i < temp.length; i++) {
            downLayer.add(temp[i]);
        }
        for (i = 0; i < considered.length; i++) {
            const node = considered[i];
            // calculate barycenter
            let sum = 0;
            let total = 0;
            for (l = 0; l < node.incoming.length; l++) {
                link = node.incoming[l];
                if (downLayer.contains(link.source)) {
                    total++;
                    sum += link.source.gridPosition;
                }
            }
            for (l = 0; l < node.outgoing.length; l++) {
                link = node.outgoing[l];
                if (downLayer.contains(link.target)) {
                    total++;
                    sum += link.target.gridPosition;
                }
            }
            if (total > 0) {
                node.dBaryCenter = sum / total;
                node.downstreamLinkCount = total;
            }
            else {
                node.dBaryCenter = i;
                node.downstreamLinkCount = 0;
            }
        }
    }
    /// <summary>
    /// Optimizes the crossings.
    /// </summary>
    /// <remarks>The big trick here is the usage of weights or values attached to connected nodes which turn a problem of crossing links
    /// to an a problem of ordering numbers.</remarks>
    /// <param name="layerIndex">The layer index.</param>
    /// <param name="movingDownwards">If set to <c>true</c> we move down in the layer stack.</param>
    /// <returns>The number of nodes having moved, i.e. the number of crossings reduced.</returns>
    optimizeLayerCrossings(down, layer) {
        let iconsidered;
        let considered;
        if (down) {
            considered = this.layers[iconsidered = layer + 1];
        }
        else {
            considered = this.layers[iconsidered = layer - 1];
        }
        // remember what it was
        const presorted = considered.slice(0);
        // calculate barycenters for all nodes in the considered layer
        if (down) {
            this.calcUpData(iconsidered);
        }
        else {
            this.calcDownData(iconsidered);
        }
        // sort nodes within this layer according to the barycenters
        considered.sort((n1, n2) => {
            const n1BaryCenter = this.calcBaryCenter(n1), n2BaryCenter = this.calcBaryCenter(n2);
            if (Math.abs(n1BaryCenter - n2BaryCenter) < 0.0001) {
                // in case of coinciding barycenters compare by the count of in/out links
                if (n1.degree() === n2.degree()) {
                    return this.compareByIndex(n1, n2);
                }
                else if (n1.degree() < n2.degree()) {
                    return 1;
                }
                return -1;
            }
            const compareValue = (n2BaryCenter - n1BaryCenter) * 1000;
            if (compareValue > 0) {
                return -1;
            }
            else if (compareValue < 0) {
                return 1;
            }
            return this.compareByIndex(n1, n2);
        });
        // count relocations
        let i, moves = 0;
        for (i = 0; i < considered.length; i++) {
            if (considered[i] !== presorted[i]) {
                moves++;
            }
        }
        if (moves > 0) {
            // now that the boxes have been arranged, update their grid positions
            let inode = 0;
            for (i = 0; i < considered.length; i++) {
                const node = considered[i];
                node.gridPosition = inode++;
            }
        }
        return moves;
    }
    /// <summary>
    /// Swaps a pair of nodes in a layer.
    /// </summary>
    /// <param name="layerIndex">Index of the layer.</param>
    /// <param name="n">The Nth node in the layer.</param>
    _swapPairs() {
        const maxIterations = this.options.layeredIterations;
        let iter = 0;
        while (true) {
            if (iter++ > maxIterations) {
                break;
            }
            const downwards = (iter % 4 <= 1);
            const secondPass = (iter % 4 === 1);
            for (let l = (downwards ? 0 : this.layers.length - 1); downwards ? l <= this.layers.length - 1 : l >= 0; l += (downwards ? 1 : -1)) {
                const layer = this.layers[l];
                let hasSwapped = false;
                // there is no need to recalculate crossings if they were calculated
                // on the previous step and nothing has changed
                let calcCrossings = true;
                let memCrossings = 0;
                for (let n = 0; n < layer.length - 1; n++) {
                    // count crossings
                    let up = 0;
                    let down = 0;
                    let crossBefore = 0;
                    if (calcCrossings) {
                        if (l !== 0) {
                            up = this.countLinksCrossingBetweenTwoLayers(l - 1, l);
                        }
                        if (l !== this.layers.length - 1) {
                            down = this.countLinksCrossingBetweenTwoLayers(l, l + 1);
                        }
                        if (downwards) {
                            up *= 2;
                        }
                        else {
                            down *= 2;
                        }
                        crossBefore = up + down;
                    }
                    else {
                        crossBefore = memCrossings;
                    }
                    if (crossBefore === 0) {
                        continue;
                    }
                    // Swap nodes
                    let node1 = layer[n];
                    let node2 = layer[n + 1];
                    let node1GridPos = node1.gridPosition;
                    let node2GridPos = node2.gridPosition;
                    layer[n] = node2;
                    layer[n + 1] = node1;
                    node1.gridPosition = node2GridPos;
                    node2.gridPosition = node1GridPos;
                    // count crossings again and if worse than before, restore swapping
                    up = 0;
                    if (l !== 0) {
                        up = this.countLinksCrossingBetweenTwoLayers(l - 1, l);
                    }
                    down = 0;
                    if (l !== this.layers.length - 1) {
                        down = this.countLinksCrossingBetweenTwoLayers(l, l + 1);
                    }
                    if (downwards) {
                        up *= 2;
                    }
                    else {
                        down *= 2;
                    }
                    const crossAfter = up + down;
                    let revert = false;
                    if (secondPass) {
                        revert = crossAfter >= crossBefore;
                    }
                    else {
                        revert = crossAfter > crossBefore;
                    }
                    if (revert) {
                        node1 = layer[n];
                        node2 = layer[n + 1];
                        node1GridPos = node1.gridPosition;
                        node2GridPos = node2.gridPosition;
                        layer[n] = node2;
                        layer[n + 1] = node1;
                        node1.gridPosition = node2GridPos;
                        node2.gridPosition = node1GridPos;
                        // nothing has changed, remember the crossings so that
                        // they are not calculated again on the next step
                        memCrossings = crossBefore;
                        calcCrossings = false;
                    }
                    else {
                        hasSwapped = true;
                        calcCrossings = true;
                    }
                }
                if (hasSwapped) {
                    if (l !== this.layers.length - 1) {
                        this.calcUpData(l + 1);
                    }
                    if (l !== 0) {
                        this.calcDownData(l - 1);
                    }
                }
            }
        }
    }
    /// <summary>
    /// Counts the number of links crossing between two layers.
    /// </summary>
    /// <param name="layerIndex1">The layer index.</param>
    /// <param name="layerIndex2">Another layer index.</param>
    /// <returns></returns>
    countLinksCrossingBetweenTwoLayers(ulayer, dlayer) {
        const links = this.layers[ulayer].linksTo[dlayer];
        let link1, link2, n11, n12, n21, n22, l1, l2;
        let crossings = 0;
        const length = links.length;
        for (l1 = 0; l1 < length; l1++) {
            link1 = links[l1];
            for (l2 = l1 + 1; l2 < length; l2++) {
                link2 = links[l2];
                if (link1.target.layer === dlayer) {
                    n11 = link1.source;
                    n12 = link1.target;
                }
                else {
                    n11 = link1.target;
                    n12 = link1.source;
                }
                if (link2.target.layer === dlayer) {
                    n21 = link2.source;
                    n22 = link2.target;
                }
                else {
                    n21 = link2.target;
                    n22 = link2.source;
                }
                const n11gp = n11.gridPosition;
                const n12gp = n12.gridPosition;
                const n21gp = n21.gridPosition;
                const n22gp = n22.gridPosition;
                if ((n11gp - n21gp) * (n12gp - n22gp) < 0) {
                    crossings++;
                }
            }
        }
        return crossings;
    }
    calcBaryCenter(node) {
        const upstreamLinkCount = node.upstreamLinkCount;
        const downstreamLinkCount = node.downstreamLinkCount;
        const uBaryCenter = node.uBaryCenter;
        const dBaryCenter = node.dBaryCenter;
        if (upstreamLinkCount > 0 && downstreamLinkCount > 0) {
            return (uBaryCenter + dBaryCenter) / 2;
        }
        if (upstreamLinkCount > 0) {
            return uBaryCenter;
        }
        if (downstreamLinkCount > 0) {
            return dBaryCenter;
        }
        return 0;
    }
    _gridPositionComparer(x, y) {
        if (x.gridPosition < y.gridPosition) {
            return -1;
        }
        if (x.gridPosition > y.gridPosition) {
            return 1;
        }
        return 0;
    }
    _positionAscendingComparer(x, y) {
        return x.k < y.k ? -1 : x.k > y.k ? 1 : 0;
    }
    _positionDescendingComparer(x, y) {
        return x.k < y.k ? 1 : x.k > y.k ? -1 : 0;
    }
    _firstVirtualNode(layer) {
        for (let c = 0; c < layer.length; c++) {
            if (layer[c].isVirtual) {
                return c;
            }
        }
        return -1;
    }
    compareByIndex(o1, o2) {
        const i1 = o1.index;
        const i2 = o2.index;
        if (i1 < i2) {
            return 1;
        }
        if (i1 > i2) {
            return -1;
        }
        return 0;
    }
    intDiv(numerator, denominator) {
        return (numerator - numerator % denominator) / denominator;
    }
    nextVirtualNode(layer, node) {
        const nodeIndex = node.layerIndex;
        for (let i = nodeIndex + 1; i < layer.length; ++i) {
            if (layer[i].isVirtual) {
                return layer[i];
            }
        }
        return null;
    }
}
